/* eslint-disable react/no-unstable-nested-components --
 * MasterDataTable の columns 配列に並ぶ cell 関数は React component ではなく
 * テーブルセルの render 関数。 親コンポーネント再 render 時に再生成されるが、
 * MasterDataTable は cell の戻り値を <TableCell> の children として表示するだけで
 * 独立した component subtree を持たないため、 lint 警告の前提 (= subtree 破棄)
 * に該当しない (ProcurementPlanTable と同じ理由)。
 */
// TODO(RBAC, parent #641 #647): admin only
import { type DragEvent, useEffect, useMemo, useState } from 'react'

import { create } from '@bufbuild/protobuf'
import { timestampDate } from '@bufbuild/protobuf/wkt'
import { Code, ConnectError } from '@connectrpc/connect'
import { WorkflowSection } from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'
import { DeleteWorkflowTemplateRequestSchema } from '@ebalance/proto/bffuser/v1/workflow_template_service_pb'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useSearchParams } from 'react-router'

import {
  Box,
  Flex,
  HStack,
  Link,
  PrimaryButton,
  type SegmentedControlOption,
  TertiaryButton,
  chakra,
} from '@enechain/ecloud-designsystem'
import { FaPlusRegular } from '@enechain/ecloud-icons'

import { AccessibleSegmentedControl } from '~/components/common/AccessibleSegmentedControl'
import { CompactAlert } from '~/components/common/CompactAlert'
import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import { DsMultiSelect } from '~/components/common/DsMultiSelect'
import { GripDotsVerticalIcon } from '~/components/common/GripDotsVerticalIcon'
import {
  DeleteIconButton,
  DuplicateIconButton,
  EditIconButton,
  MasterDataTable,
  type MasterDataTableColumn,
} from '~/components/common/MasterDataTable'
import { PageBody } from '~/components/common/PageBody'
import type { PageBreadcrumbItem } from '~/components/common/PageBreadcrumb'
import { PageIntro } from '~/components/common/PageIntro'
import { PageLayout } from '~/components/common/PageLayout'
import { PageScrollArea } from '~/components/common/PageScrollArea'
import { RegionLoading } from '~/components/common/RegionLoading'
import { WorkflowCaseTagChip } from '~/components/common/WorkflowCaseTagChip'
import { FilterSection, FilterToolbar } from '~/components/common/filters'
import { iconSize } from '~/constants/iconSize'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { formatJstDateTimeHm } from '~/models/dateTime'
import { localizeBuiltinWorkflowText } from '~/models/workflowBuiltinText'
import {
  WORKFLOW_SECTION_ITEMS,
  canonicalWorkflowSection,
  workflowSectionLabelKey,
} from '~/models/workflowSection'
import { WorkflowTemplateEdit } from '~/pages/AdminWorkflowTemplates/Edit'
import { AutoCreationRuleSection } from '~/pages/AdminWorkflowTemplates/components/AutoCreationRuleSection'
import { useDeleteWorkflowTemplate } from '~/pages/AdminWorkflowTemplates/hooks/useDeleteWorkflowTemplate'
import {
  type WorkflowTemplateRow,
  convertListWorkflowTemplatesResponse,
  useListWorkflowTemplates,
} from '~/pages/AdminWorkflowTemplates/hooks/useListWorkflowTemplates'
import { usePersistedWorkflowTemplateFilters } from '~/pages/AdminWorkflowTemplates/hooks/usePersistedWorkflowTemplateFilters'
import { useUpdateWorkflowTemplateDisplayOrder } from '~/pages/AdminWorkflowTemplates/hooks/useUpdateWorkflowTemplateDisplayOrder'
import {
  FILTER_VISIBLE_SECTIONS,
  pruneUnavailableTags,
} from '~/pages/AdminWorkflowTemplates/models/persistedFilters'
import { routePaths } from '~/routes/routePaths'

/**
 * 行の並べ替え中に差し込む挿入位置の線 (inset box-shadow)。 `style` に渡す CSS 値なので
 * Chakra の色 prop は使えず、 semantic token の CSS 変数 (`Text.midEmphasis`) を直接引く。
 * 値の直書き (light mode 固定の hex) だと dark mode で色が追随しない。
 */
const DROP_LINE_COLOR = 'var(--chakra-colors--text-mid-emphasis)'

/**
 * 計画断面フィルタの選択肢。旧当日系は単日へ正規化して表示する。
 *
 * 出す値の SoT は `models/persistedFilters.ts` の [[FILTER_VISIBLE_SECTIONS]]。
 * **UI の選択肢と復元の妥当集合を同じ列から作る** — 別々に持つと、広すぎれば
 * 画面に無い断面を復元して選択が消え (review 指摘 #6969)、狭すぎれば選べる値を
 * 復元で捨てる (同 #6945)。
 */
const TEMPLATE_FILTER_SECTION_ITEMS = WORKFLOW_SECTION_ITEMS.filter((item) =>
  FILTER_VISIBLE_SECTIONS.includes(item.value),
)

type ViewMode =
  | { kind: 'list' }
  | { kind: 'create' }
  | { kind: 'edit'; row?: WorkflowTemplateRow }
  | { kind: 'duplicate'; sourceRow: WorkflowTemplateRow }

type DeleteState =
  | { kind: 'none' }
  | { kind: 'confirm'; row: WorkflowTemplateRow }

/**
 * 計画断面フィルタの「すべて」= フィルタ解除を表す sentinel。
 */
const ALL_SECTIONS = 'all'

/** タグフィルタの選択肢。 value は backend / DB で保持されている label そのまま、
 *  displayLabel は UI 表示用（プリセットのみ i18n 翻訳を適用）。 */
type TagFilterOption = {
  value: string
  displayLabel: string
  color: string
}

const PRESET_TAG_VALUE_REGULAR = '定期'
const PRESET_TAG_VALUE_CHANGE = '変更'

export const AdminWorkflowTemplatesPage = (): React.ReactElement => {
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  const [searchParams, setSearchParams] = useSearchParams()
  const { data, isLoading, isError, refetch } = useListWorkflowTemplates()
  const { mutateAsync: deleteAsync, isPending: isDeleting } =
    useDeleteWorkflowTemplate()
  const {
    mutate: updateOrder,
    isPending: isReordering,
    error: reorderError,
  } = useUpdateWorkflowTemplateDisplayOrder()

  const requestedTemplateId = searchParams.get('edit') ?? undefined
  const [viewMode, setViewMode] = useState<ViewMode>({ kind: 'list' })
  const [deleteState, setDeleteState] = useState<DeleteState>({ kind: 'none' })
  const [deleteError, setDeleteError] = useState<string | undefined>(undefined)
  // Edit の create/update mutation pending。パンくずの中間項目を保存中は
  // 非リンク化するために使う (review 指摘 #7007、詳細は breadcrumbItems 参照)。
  const [isFormSaving, setIsFormSaving] = useState(false)
  // 業務区分・タグは再訪をまたいで保持する (AC-SC-031 [P0])。
  // 保持対象と除外の理由は `models/persistedFilters.ts` の docstring 参照。
  const {
    filterSection,
    setFilterSection,
    selectedTagLabels,
    setSelectedTagLabels,
  } = usePersistedWorkflowTemplateFilters()
  const [draggedRowId, setDraggedRowId] = useState<string | null>(null)
  const [dragOverRowId, setDragOverRowId] = useState<string | null>(null)
  const [dragOverPosition, setDragOverPosition] = useState<'top' | 'bottom'>(
    'bottom',
  )

  const rows = useMemo(
    () =>
      data !== undefined ? convertListWorkflowTemplatesResponse(data) : [],
    [data],
  )
  const autoCreationRuleTemplates = useMemo(
    () =>
      rows.map((row) => ({ id: row.id, name: row.name, section: row.section })),
    [rows],
  )
  const effectiveViewMode: ViewMode =
    requestedTemplateId !== undefined
      ? {
          kind: 'edit',
          row: rows.find((row) => row.id === requestedTemplateId),
        }
      : viewMode

  const clearEditQuery = (): void => {
    const nextSearchParams = new URLSearchParams(searchParams)
    nextSearchParams.delete('edit')
    setSearchParams(nextSearchParams)
  }

  // AC-WFT-001: backend が display_order 昇順で返す。 frontend は filter のみ。
  const isFilterActive = filterSection !== null || selectedTagLabels.size > 0

  const filteredRows = useMemo(() => {
    let result = rows
    if (filterSection !== null) {
      result = result.filter(
        (r) => canonicalWorkflowSection(r.section) === filterSection,
      )
    }
    if (selectedTagLabels.size > 0) {
      result = result.filter((r) =>
        r.defaultTags.some((tag) => selectedTagLabels.has(tag.label)),
      )
    }
    return result
  }, [rows, filterSection, selectedTagLabels])

  // タグフィルタ候補: プリセット (定期=青, 変更=赤) + 一覧の default_tags
  const tagFilterOptions = useMemo<TagFilterOption[]>(() => {
    const rowTagSeen = new Set<string>()
    const fromRows: TagFilterOption[] = []
    for (const row of rows) {
      for (const tag of row.defaultTags) {
        if (!rowTagSeen.has(tag.label)) {
          rowTagSeen.add(tag.label)
          fromRows.push({
            value: tag.label,
            displayLabel: localizeBuiltinWorkflowText(tag.label, t),
            color: tag.color,
          })
        }
      }
    }
    fromRows.sort((a, b) => a.value.localeCompare(b.value, 'ja'))
    const presets: TagFilterOption[] = [
      {
        value: PRESET_TAG_VALUE_REGULAR,
        displayLabel: t('page:adminWorkflowTemplates.schedule.regular'),
        color: 'blue',
      },
      {
        value: PRESET_TAG_VALUE_CHANGE,
        displayLabel: t('page:adminWorkflowTemplates.schedule.change'),
        color: 'red',
      },
    ]
    const seen = new Set<string>()
    const result: TagFilterOption[] = []
    for (const option of [...presets, ...fromRows]) {
      if (!seen.has(option.value)) {
        seen.add(option.value)
        result.push(option)
      }
    }
    return result
  }, [rows, t])

  // 候補から消えたタグの選択を落とす。
  //
  // `DsMultiSelect` は現行 options に無い値をチップに出さないため、削除済みタグを
  // 抱えたままだと**一覧は 0 件なのに選択が画面に見えず解除もできない**
  // (review 指摘 #6969)。
  //
  // **取得完了を待つ** (`data === undefined` の間は何もしない)。取得前は候補が
  // プリセットのみなので、早すぎると正当な選択まで消して、しかもその結果が保存される
  // (特異日マスタ #6910 / 48点データ #6932 で踏んだのと同じ罠)。
  useEffect(() => {
    if (data === undefined) return
    const pruned = pruneUnavailableTags(
      selectedTagLabels,
      tagFilterOptions.map((option) => option.value),
    )
    // 変化が無ければ同じ参照が返るので、無駄な保存を誘発しない。
    if (pruned !== selectedTagLabels) setSelectedTagLabels(pruned)
    // `setSelectedTagLabels` は保持 hook が毎レンダー新しい関数を返すため依存に
    // 入れない (入れると effect が毎レンダー走る)。
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 上記理由
  }, [data, selectedTagLabels, tagFilterOptions])

  const canDrag = !isFilterActive && !isReordering
  const draggedIndex = useMemo(
    () =>
      draggedRowId !== null
        ? filteredRows.findIndex((r) => r.id === draggedRowId)
        : -1,
    [draggedRowId, filteredRows],
  )

  const getRowStyle = (
    row: WorkflowTemplateRow,
  ): React.CSSProperties | undefined => {
    if (!canDrag || draggedRowId === null || draggedIndex === -1)
      return undefined
    if (row.id === draggedRowId || dragOverRowId !== row.id) return undefined
    return {
      boxShadow:
        dragOverPosition === 'bottom'
          ? `inset 0 -2px 0 0 ${DROP_LINE_COLOR}`
          : `inset 0 2px 0 0 ${DROP_LINE_COLOR}`,
    }
  }

  const handleDeleteConfirm = async (): Promise<void> => {
    if (deleteState.kind !== 'confirm') return
    const { row } = deleteState
    try {
      await deleteAsync(
        create(DeleteWorkflowTemplateRequestSchema, {
          id: row.id,
          // 未上書きの初期雛形は updatedAt を持たない → expectedUpdatedAt を
          // 省略し (proto optional)、backend の SoftDelete tombstone 経路
          // (楽観ロックなし) へ通す (issue #6356)。
          expectedUpdatedAt: row.updatedAt,
        }),
      )
      setDeleteState({ kind: 'none' })
      setDeleteError(undefined)
      // 2026-08-28 デザイナー指摘「削除・追加・編集の成功時は全画面共通で toast」。
      showSuccessToast({ title: t('common:toast.deleted') })
    } catch (err) {
      // 楽観ロック衝突 (別ユーザーが先に更新 / 未上書き初期雛形を開いている
      // 間に overlay された場合) は時間経過で解消しないため、汎用 deleteFailed
      // でなく「再読み込みして再試行」を促す (issue #6356 PR #6393 review C3)。
      if (err instanceof ConnectError && err.code === Code.Aborted) {
        setDeleteError(t('page:adminWorkflowTemplates.serverError.aborted'))
      } else {
        setDeleteError(
          t('page:adminWorkflowTemplates.serverError.deleteFailed'),
        )
      }
    }
  }

  const isFormMode = effectiveViewMode.kind !== 'list'

  // パンくず: 一覧モードは固定 2 階層。 フォームモード (edit/create/duplicate)
  // では「ワークフロー雛形」を一覧へ戻るリンクにし、末尾に葉ラベルを足して
  // 3 階層にする (design `case-templates.html` `_setBreadcrumb` 相当。issue
  // #7001)。 duplicate は Edit.tsx の isCreate 判定 (row === undefined) と
  // 同じロジックで「新規雛形」側に寄せる (フォームのパネル見出しと表記を揃える)。
  const breadcrumbItems: PageBreadcrumbItem[] = isFormMode
    ? [
        // 先頭の「業務ワークフロー」はグループの先頭画面への自動リンクになる (#10126)。
        // 保存中は中間項目と同じく離脱させないので、文字だけにする (#7007)。
        { label: t('common:nav.workflow'), noLink: isFormSaving },
        {
          label: t('common:route.adminWorkflowTemplates'),
          // 保存中 (Edit の isPending) は onClick を渡さずクリック不可にする。
          // キャンセルボタン (Edit.tsx cancelButton: disabled={isPending}) と
          // 挙動を揃え、離脱によるアンマウントで保存失敗時のエラー表示・入力
          // 内容を失わせない (issue #7007 review 指摘)。中間の項目は PageBreadcrumb が
          // 自動でリンクにしないので、to/onClick/bold のいずれも無ければただのテキスト
          // として描画され (`components/common/PageBreadcrumb.tsx`)、押せる見た目のまま
          // 無反応になる silent no-op を避けられる。
          ...(isFormSaving
            ? {}
            : {
                onClick: () => {
                  setViewMode({ kind: 'list' })
                  clearEditQuery()
                },
              }),
        },
        {
          label:
            effectiveViewMode.kind === 'edit'
              ? t('page:adminWorkflowTemplates.form.editTitle')
              : t('page:adminWorkflowTemplates.action.create'),
          bold: true,
        },
      ]
    : [
        { label: t('common:nav.workflow') },
        { label: t('common:route.adminWorkflowTemplates'), bold: true },
      ]

  const deleteRow = deleteState.kind === 'confirm' ? deleteState.row : null

  const sectionOptions: Array<SegmentedControlOption<string>> = [
    { value: ALL_SECTIONS, label: t('page:adminWorkflowTemplates.filter.all') },
    ...TEMPLATE_FILTER_SECTION_ITEMS.map((item) => ({
      value: String(item.value),
      label: t(item.adminLabelKey),
    })),
  ]

  // 全幅 7 列テーブルの列定義 (共有 `MasterDataTable` へ委譲)。
  const columns: Array<MasterDataTableColumn<WorkflowTemplateRow>> = [
    {
      key: 'name',
      header: t('page:adminWorkflowTemplates.column.name'),
      align: 'left',
      cell: (row) => (
        <chakra.span fontWeight="medium" whiteSpace="nowrap">
          {localizeBuiltinWorkflowText(row.name, t)}
        </chakra.span>
      ),
    },
    {
      key: 'section',
      header: t('page:adminWorkflowTemplates.column.section'),
      align: 'center',
      cell: (row) => {
        const sectionKey = workflowSectionLabelKey(
          'adminWorkflowTemplates',
          row.section,
        )
        return (
          <chakra.span whiteSpace="nowrap">
            {sectionKey !== undefined ? t(sectionKey) : '—'}
          </chakra.span>
        )
      },
    },
    {
      key: 'defaultTags',
      header: t('page:adminWorkflowTemplates.column.defaultTags'),
      align: 'left',
      cell: (row) => <WorkflowCaseTagChipList tags={row.defaultTags} />,
    },
    {
      key: 'bigStepCount',
      header: t('page:adminWorkflowTemplates.column.bigStepCount'),
      align: 'right',
      cell: (row) => (
        <chakra.span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {row.bigStepCount}
        </chakra.span>
      ),
    },
    {
      key: 'subStepTotalCount',
      header: t('page:adminWorkflowTemplates.column.subStepTotalCount'),
      align: 'right',
      cell: (row) => (
        <chakra.span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {row.subStepTotalCount}
        </chakra.span>
      ),
    },
    {
      key: 'updatedAt',
      header: t('page:adminWorkflowTemplates.column.updatedAt'),
      align: 'center',
      cell: (row) => {
        const updatedAtDate =
          row.updatedAt !== undefined ? timestampDate(row.updatedAt) : undefined
        return (
          <chakra.span color="Text.midEmphasis" whiteSpace="nowrap">
            {updatedAtDate !== undefined
              ? formatJstDateTimeHm(updatedAtDate)
              : '—'}
          </chakra.span>
        )
      },
    },
  ]

  const dragHandleColumn = useMemo(
    () => ({
      key: 'displayOrder',
      width: '36px',
      render: (
        _row: WorkflowTemplateRow,
        { canDrag }: { canDrag: boolean },
      ) => (
        <chakra.span
          alignItems="center"
          display="inline-flex"
          color="Text.midEmphasis"
          opacity={canDrag ? 1 : 0.4}
        >
          {/* 掴んで並べ替える面なので grip。 上下矢印は「並び順」 の表示であって
              「掴める」 ことを示さない。
              [DS gap] ecloud-icons にドットの grip が無いため暫定の自前 icon を使う
              (2026-08-24 デザイナー判断: DS に今後追加する前提)。 詳細は
              `GripDotsVerticalIcon` の JSDoc。 */}
          <GripDotsVerticalIcon
            boxSize={iconSize.gridInline}
            aria-label={t('page:adminWorkflowTemplates.action.reorder')}
          />
        </chakra.span>
      ),
    }),
    [t],
  )

  const handleDragStart = (row: WorkflowTemplateRow): void => {
    setDraggedRowId(row.id)
  }

  const handleDragEnd = (): void => {
    setDraggedRowId(null)
    setDragOverRowId(null)
    setDragOverPosition('bottom')
  }

  const handleDragOver = (
    row: WorkflowTemplateRow,
    event: DragEvent<HTMLTableRowElement>,
  ): void => {
    const rect = event.currentTarget.getBoundingClientRect()
    const isBottomHalf = event.clientY > rect.top + rect.height / 2
    setDragOverRowId((prev) => (prev === row.id ? prev : row.id))
    setDragOverPosition(isBottomHalf ? 'bottom' : 'top')
  }

  const handleDrop = (
    row: WorkflowTemplateRow,
    event: DragEvent<HTMLTableRowElement>,
  ): void => {
    if (draggedRowId === null || draggedRowId === row.id) {
      setDraggedRowId(null)
      setDragOverRowId(null)
      setDragOverPosition('bottom')
      return
    }
    const from = draggedIndex
    const to = filteredRows.findIndex((r) => r.id === row.id)
    if (from === -1 || to === -1 || from === to) {
      setDraggedRowId(null)
      setDragOverRowId(null)
      setDragOverPosition('bottom')
      return
    }

    const rect = event.currentTarget.getBoundingClientRect()
    const isBottomHalf = event.clientY > rect.top + rect.height / 2
    const reordered = [...filteredRows]
    const [moved] = reordered.splice(from, 1)
    const targetNewIndex = from < to ? to - 1 : to
    const insertIndex = isBottomHalf ? targetNewIndex + 1 : targetNewIndex
    reordered.splice(insertIndex, 0, moved)
    updateOrder({
      orderedTemplateIds: reordered.map((r) => r.id),
    })
    setDraggedRowId(null)
    setDragOverRowId(null)
    setDragOverPosition('bottom')
  }

  return (
    <PageLayout
      breadcrumbItems={breadcrumbItems}
      notices={
        isFormMode || (!isError && reorderError === null) ? undefined : (
          <>
            {isError && (
              <CompactAlert
                actions={
                  <TertiaryButton
                    onClick={() => {
                      void refetch()
                    }}
                    size="sm"
                  >
                    {t('page:adminWorkflowTemplates.list.retry')}
                  </TertiaryButton>
                }
                description={t(
                  'page:adminWorkflowTemplates.list.fetchFailedDesc',
                )}
                status="error"
                title={t('page:adminWorkflowTemplates.list.fetchFailedTitle')}
              />
            )}
            {reorderError !== null && (
              <CompactAlert
                description={t(
                  'page:adminWorkflowTemplates.list.reorderFailedDesc',
                )}
                role="alert"
                status="error"
                title={t('page:adminWorkflowTemplates.list.reorderFailedTitle')}
              />
            )}
          </>
        )
      }
    >
      <PageBody>
        {/* ヘッダー: 説明 + 新規ボタン (一覧モードのみ) */}
        {!isFormMode && (
          <Flex
            alignItems="flex-start"
            justifyContent="space-between"
            gap="12px"
          >
            <PageIntro
              description={
                <>
                  {t('page:adminWorkflowTemplates.description.prefix')}
                  <Link asChild>
                    <RouterLink to={routePaths.workflowCases.path}>
                      {t('page:adminWorkflowTemplates.description.linkLabel')}
                    </RouterLink>
                  </Link>
                  {t('page:adminWorkflowTemplates.description.suffix')}
                </>
              }
            />
            <PrimaryButton
              flexShrink={0}
              onClick={() => {
                setViewMode({ kind: 'create' })
              }}
            >
              <FaPlusRegular />
              {t('page:adminWorkflowTemplates.action.create')}
            </PrimaryButton>
          </Flex>
        )}

        {/* 編集フォーム (= フォームモード時は全幅で表示) */}
        {isFormMode && (
          <PageScrollArea>
            <WorkflowTemplateEdit
              row={
                effectiveViewMode.kind === 'edit'
                  ? effectiveViewMode.row
                  : undefined
              }
              onClose={() => {
                setIsFormSaving(false)
                setViewMode({ kind: 'list' })
                clearEditQuery()
              }}
              onPendingChange={setIsFormSaving}
              sourceRow={
                effectiveViewMode.kind === 'duplicate'
                  ? effectiveViewMode.sourceRow
                  : undefined
              }
              templateId={requestedTemplateId}
            />
          </PageScrollArea>
        )}

        {/* フィルタバー + 全幅テーブル (= 一覧モードのみ表示) */}
        {!isFormMode && (
          <>
            {/* フィルタバー (= 計画断面 + タグ) */}
            <FilterToolbar>
              <FilterSection
                label={t('page:adminWorkflowTemplates.filter.section')}
              >
                <AccessibleSegmentedControl
                  aria-label={t('page:adminWorkflowTemplates.filter.section')}
                  name="admin-workflow-templates-section"
                  onSelect={(value) => {
                    setFilterSection(
                      value === ALL_SECTIONS
                        ? null
                        : (Number(value) as WorkflowSection),
                    )
                  }}
                  options={sectionOptions}
                  selected={
                    filterSection === null
                      ? ALL_SECTIONS
                      : String(filterSection)
                  }
                />
              </FilterSection>
              <FilterSection
                label={t('page:adminWorkflowTemplates.filter.tag')}
              >
                {/* タグは chip の横並びではなく multi select にする
                    (2026-08-24 デザイナー指摘)。 タグは組織ごとに増えるため、
                    chip だとフィルタバーが何行にも折り返して一覧が押し下げられる。
                    option には色 dot を置かない (同日デザイナー判断。 タグ色は
                    一覧のチップ側で見えるので、 list 内の凡例は冗長)。 */}
                <DsMultiSelect
                  minWidth="180px"
                  aria-label={t('page:adminWorkflowTemplates.filter.tag')}
                  onChange={setSelectedTagLabels}
                  options={tagFilterOptions.map((option) => ({
                    value: option.value,
                    label: option.displayLabel,
                  }))}
                  placeholder={t('page:adminWorkflowTemplates.filter.all')}
                  selected={selectedTagLabels}
                />
              </FilterSection>
            </FilterToolbar>

            {/* 全幅テーブル (drag handle + 6 列 + 操作列) */}
            <RegionLoading
              flexDirection="column"
              flex="1"
              display="flex"
              minHeight={isLoading && data === undefined ? '200px' : 0}
              isLoading={isLoading && data === undefined}
            >
              <PageScrollArea>
                {isLoading && data === undefined ? null : (
                  <Flex flexDirection="column" gap={8}>
                    {filteredRows.length === 0 ? (
                      <Box
                        padding="24px"
                        color="Text.midEmphasis"
                        fontSize="sm"
                        textAlign="center"
                        borderWidth="1px"
                        borderColor="Border.lowEmphasis"
                        borderRadius="lg"
                      >
                        {t('page:adminWorkflowTemplates.list.empty')}
                      </Box>
                    ) : (
                      <MasterDataTable
                        actionColWidth="128px"
                        canDragRows={canDrag}
                        columns={columns}
                        dragHandleColumn={dragHandleColumn}
                        getRowKey={(row) => row.id}
                        getRowStyle={getRowStyle}
                        isRowDisabled={() => isReordering}
                        onRowClick={(row) => {
                          setViewMode({ kind: 'edit', row })
                        }}
                        onRowDragEnd={handleDragEnd}
                        onRowDragOver={handleDragOver}
                        onRowDragStart={handleDragStart}
                        onRowDrop={handleDrop}
                        renderRowActions={(row) => (
                          <>
                            <EditIconButton
                              ariaLabel={t('common:action.edit')}
                              onClick={() => {
                                setViewMode({ kind: 'edit', row })
                              }}
                            />
                            <DuplicateIconButton
                              ariaLabel={t(
                                'page:adminWorkflowTemplates.action.duplicate',
                              )}
                              onClick={() => {
                                setViewMode({
                                  kind: 'duplicate',
                                  sourceRow: row,
                                })
                              }}
                            />
                            <DeleteIconButton
                              ariaLabel={t('common:action.delete')}
                              onClick={() => {
                                setDeleteError(undefined)
                                setDeleteState({ kind: 'confirm', row })
                              }}
                            />
                          </>
                        )}
                        rows={filteredRows}
                      />
                    )}
                    {/* 雛形の名前・計画断面で表示するので、雛形の一覧を取得できた時だけ出す */}
                    {data !== undefined && (
                      <AutoCreationRuleSection
                        templates={autoCreationRuleTemplates}
                      />
                    )}
                  </Flex>
                )}
              </PageScrollArea>
            </RegionLoading>
          </>
        )}
      </PageBody>

      {/* 削除確認ダイアログ */}
      {deleteRow !== null && (
        <ConfirmDialog
          body={
            deleteError ??
            t('page:adminWorkflowTemplates.delete.confirmBody', {
              name: localizeBuiltinWorkflowText(deleteRow.name, t),
            })
          }
          confirmLabel={t('page:adminWorkflowTemplates.delete.confirmLabel')}
          destructive
          isLoading={isDeleting}
          isOpen={deleteState.kind === 'confirm'}
          onCancel={() => {
            setDeleteState({ kind: 'none' })
            setDeleteError(undefined)
          }}
          onConfirm={() => {
            void handleDeleteConfirm()
          }}
          title={t('page:adminWorkflowTemplates.delete.confirmTitle')}
        />
      )}
    </PageLayout>
  )
}

/** 一行に複数タグチップを並べる (一覧タグ列専用)。 */
const WorkflowCaseTagChipList = ({
  tags,
}: {
  tags: ReadonlyArray<{ label: string; color: string }>
}): React.ReactElement => {
  if (tags.length === 0) {
    return <chakra.span color="Text.midEmphasis">-</chakra.span>
  }
  return (
    <HStack flexWrap="wrap" gap={1}>
      {tags.map((tag) => (
        <WorkflowCaseTagChip
          key={tag.label}
          color={tag.color}
          label={tag.label}
        />
      ))}
    </HStack>
  )
}
